/**
 * ValueAtStakePanel — captures the indicative value-at-stake inputs for a run
 * and shows the computed low–mid–high range. Range-based and explicitly
 * labelled indicative (not a benefits commitment), per the board-defensible
 * methodology. Saves via PUT /admin/assessments/runs/{runId}/value-at-stake.
 */
import React, { useCallback, useEffect, useState } from "react";
import { PoundSterling, ChevronDown, ChevronUp, Save } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const VALUE_BASE_TYPES = [
  "Annual IT budget", "Annual operating cost", "Revenue influenced",
  "Contract spend", "Programme portfolio value", "Supplier spend",
  "Service cost base", "Risk exposure estimate",
];

const DEFAULTS = {
  annual_value_base_gbp: "", value_base_type: "Annual IT budget", value_base_description: "",
  target_score: "", leverage_low_pct: 10, leverage_mid_pct: 15, leverage_high_pct: 20,
  confidence_factor_pct: 100, horizon_years: 3, value_at_stake_override_gbp: "", value_at_stake_basis: "",
  programme_cost_gbp: "", roi_multiple: "",
};

const numOrNull = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

export default function ValueAtStakePanel({ runId }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(DEFAULTS);
  const [computed, setComputed] = useState(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/assessments/runs/${runId}`);
      const v = data.value_at_stake;
      if (v) {
        setForm({ ...DEFAULTS, ...Object.fromEntries(Object.entries(v).map(([k, val]) => [k, val ?? DEFAULTS[k] ?? ""])) });
        setComputed(data.value_at_stake_computed || null);
      }
    } catch { /* run not loaded yet */ }
  }, [runId]);

  useEffect(() => { reload(); }, [reload]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        annual_value_base_gbp: numOrNull(form.annual_value_base_gbp),
        value_base_type: form.value_base_type || null,
        value_base_description: form.value_base_description || null,
        target_score: numOrNull(form.target_score),
        leverage_low_pct: Number(form.leverage_low_pct),
        leverage_mid_pct: Number(form.leverage_mid_pct),
        leverage_high_pct: Number(form.leverage_high_pct),
        confidence_factor_pct: Number(form.confidence_factor_pct),
        horizon_years: Number(form.horizon_years),
        value_at_stake_override_gbp: numOrNull(form.value_at_stake_override_gbp),
        value_at_stake_basis: form.value_at_stake_basis || null,
        programme_cost_gbp: numOrNull(form.programme_cost_gbp),
        roi_multiple: numOrNull(form.roi_multiple),
      };
      const { data } = await api.put(`/admin/assessments/runs/${runId}/value-at-stake`, payload);
      setComputed(data.value_at_stake_computed || null);
      toast.success("Value at stake saved — appears on the report money page");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save value at stake");
    } finally { setBusy(false); }
  };

  const fieldCls = "w-full border border-zinc-300 px-2.5 py-1.5 text-[13px]";
  const lblCls = "text-[11px] font-medium text-zinc-600";

  return (
    <div className="bg-white border border-zinc-200 p-5" data-testid="value-at-stake-panel">
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center justify-between gap-2" data-testid="vas-toggle">
        <span className="flex items-center gap-2 text-zinc-700 text-[12px] font-medium uppercase tracking-[0.16em]"><PoundSterling size={14}/> Value at stake</span>
        {open ? <ChevronUp size={16} className="text-zinc-400"/> : <ChevronDown size={16} className="text-zinc-400"/>}
      </button>

      {computed && (
        <div className="mt-3 bg-[#0B0B0C] text-white p-3" data-testid="vas-computed">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#A179FA]">Indicative · {computed.has_range ? "range" : computed.has_roi ? "programme-cost basis" : "stated"}</div>
          <div className="text-xl font-semibold mt-1" data-testid="vas-headline">
            {computed.has_range ? computed.range_label : computed.headline_label}
          </div>
          {computed.has_range && <div className="text-[12px] text-[#C0A8F8]">Midpoint {computed.mid_label} · {computed.horizon_years}-yr horizon</div>}
          {computed.has_roi && <div className="text-[12px] text-[#C0A8F8]">{computed.programme_cost_label} × {computed.roi_multiple}× · pending a validated base</div>}
        </div>
      )}

      {open && (
        <div className="mt-4 space-y-3" data-testid="vas-form">
          <p className="text-[11px] text-zinc-500 leading-relaxed">Indicative only — the portion of the value pool that may be addressable over the horizon. Not a benefits commitment until validated by a business case.</p>

          <div>
            <label className={lblCls}>Annual value base (£)</label>
            <input type="number" min="0" value={form.annual_value_base_gbp} onChange={(e) => set("annual_value_base_gbp", e.target.value)} className={fieldCls} placeholder="e.g. 20000000" data-testid="vas-base" />
          </div>
          <div>
            <label className={lblCls}>Value base type</label>
            <select value={form.value_base_type} onChange={(e) => set("value_base_type", e.target.value)} className={`${fieldCls} bg-white`} data-testid="vas-base-type">
              {VALUE_BASE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className={lblCls}>Value base note (optional)</label>
            <input value={form.value_base_description} onChange={(e) => set("value_base_description", e.target.value)} className={fieldCls} placeholder="Source / scope of the figure" data-testid="vas-base-desc" />
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div><label className={lblCls}>Leverage low %</label><input type="number" min="0" max="100" value={form.leverage_low_pct} onChange={(e) => set("leverage_low_pct", e.target.value)} className={fieldCls} data-testid="vas-lev-low" /></div>
            <div><label className={lblCls}>Mid %</label><input type="number" min="0" max="100" value={form.leverage_mid_pct} onChange={(e) => set("leverage_mid_pct", e.target.value)} className={fieldCls} data-testid="vas-lev-mid" /></div>
            <div><label className={lblCls}>High %</label><input type="number" min="0" max="100" value={form.leverage_high_pct} onChange={(e) => set("leverage_high_pct", e.target.value)} className={fieldCls} data-testid="vas-lev-high" /></div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div><label className={lblCls}>Confidence %</label><input type="number" min="0" max="100" value={form.confidence_factor_pct} onChange={(e) => set("confidence_factor_pct", e.target.value)} className={fieldCls} data-testid="vas-conf" /></div>
            <div><label className={lblCls}>Horizon (yrs)</label><input type="number" min="0" max="10" step="0.5" value={form.horizon_years} onChange={(e) => set("horizon_years", e.target.value)} className={fieldCls} data-testid="vas-horizon" /></div>
            <div><label className={lblCls}>Target score</label><input type="number" min="0" max="5" step="0.1" value={form.target_score} onChange={(e) => set("target_score", e.target.value)} className={fieldCls} placeholder="auto" data-testid="vas-target" /></div>
          </div>

          <div className="border-t border-zinc-100 pt-3">
            <label className={lblCls}>Direct override (£) — optional</label>
            <input type="number" min="0" value={form.value_at_stake_override_gbp} onChange={(e) => set("value_at_stake_override_gbp", e.target.value)} className={fieldCls} placeholder="Use a CFO-validated figure" data-testid="vas-override" />
            <input value={form.value_at_stake_basis} onChange={(e) => set("value_at_stake_basis", e.target.value)} className={`${fieldCls} mt-2`} placeholder="Basis for the override" data-testid="vas-override-basis" />
          </div>

          <div className="border-t border-zinc-100 pt-3">
            <label className={lblCls}>Programme-cost anchor (£) — fallback when no value base</label>
            <p className="text-[10px] text-zinc-400 mb-1 leading-relaxed">Only used if no annual value base or override is set. Derives an indicative figure = programme cost × ROI multiple, so the money page is never blank. Not a client-validated base — does not clear the benefits gateway.</p>
            <div className="grid grid-cols-2 gap-2">
              <div><label className={lblCls}>Programme cost (£)</label><input type="number" min="0" value={form.programme_cost_gbp} onChange={(e) => set("programme_cost_gbp", e.target.value)} className={fieldCls} placeholder="e.g. 750000" data-testid="vas-programme-cost" /></div>
              <div><label className={lblCls}>ROI multiple (×)</label><input type="number" min="0" max="50" step="0.5" value={form.roi_multiple} onChange={(e) => set("roi_multiple", e.target.value)} className={fieldCls} placeholder="default 3" data-testid="vas-roi-multiple" /></div>
            </div>
          </div>

          <button type="button" onClick={save} disabled={busy} className="ek-btn ek-btn-primary w-full text-[13px] disabled:opacity-50" data-testid="vas-save"><Save size={13}/> {busy ? "Saving…" : "Save value at stake"}</button>
        </div>
      )}
    </div>
  );
}
